<template>
  <div class="app" :class="[{ 'menu-open': menuOpen }, theme]">
    <!-- Navigation -->
    <nav class="nav" :class="{ scrolled: scrolled }">
      <div class="nav-logo">
        <span class="logo-pulse"></span>
        Js<span class="logo-dot">-</span>Werner<span class="logo-dot">.de</span>
      </div>
      <div class="nav-actions">
        <button class="theme-toggle" @click="toggleTheme" :title="theme === 'dark' ? 'Light Mode' : 'Dark Mode'">
          <Icon :icon="theme === 'dark' ? 'ph:sun-bold' : 'ph:moon-bold'" width="20"/>
        </button>
        <div class="nav-menu-icon" @click="menuOpen = !menuOpen">
          <span></span>
          <span></span>
        </div>
      </div>
    </nav>

    <!-- Fullscreen Menu -->
    <div class="menu-overlay">
      <div class="menu-links">
        <a v-for="(link, i) in menuLinks"
           :key="i"
           :href="link.href"
           @click="menuOpen = false"
           :style="{ transitionDelay: menuOpen ? `${i * 0.1}s` : '0s' }">
          {{ link.name }}
          <Icon icon="ph:arrow-up-right" class="menu-arrow" width="20"/>
        </a>
      </div>
      <div class="menu-decoration">
        <svg viewBox="0 0 400 100" class="ekg-svg">
          <polyline points="0,50 80,50 100,50 110,20 120,80 130,50 400,50"
                    fill="none" stroke="currentColor" stroke-width="2"/>
        </svg>
      </div>
    </div>

    <!-- Hero Section -->
    <section class="hero">
      <div class="hero-grid">
        <div class="hero-text">
          <div class="hero-badge">
            <span class="badge-dot"></span>
            <span>Pflegefachkraft in Ausbildung</span>
            <span class="badge-separator">|</span>
            <span>Vinzenzkrankenhaus Hannover</span>
          </div>

          <h1 class="hero-name">
            <span class="name-line outline">Jonathan</span>
            <span class="name-line filled">Sevan Werner</span>
          </h1>

          <p class="hero-title">
            <span class="title-icon">
              <Icon icon="ph:heart-straight-fill" width="18"/>
            </span>
            Mit Herz und Kompetenz – Ausbildung zur Pflegefachkraft
          </p>
          <p class="hero-motto">
            Vom Rechtswesen in die Pflege – weil Menschlichkeit der beste Karriereweg ist.
          </p>

          <div class="hero-stats">
            <div class="hero-stat">
              <span class="stat-number">{{ calculatedAge }}</span>
              <span class="stat-label">Jahre jung</span>
            </div>
            <div class="hero-stat">
              <span class="stat-number">{{ trainingYear }}. Jahr</span>
              <span class="stat-label">Ausbildungsjahr</span>
            </div>
            <div class="hero-stat">
              <span class="stat-number">3</span>
              <span class="stat-label">Sprachen</span>
            </div>
          </div>

          <div class="hero-cta">
            <button class="cta-primary" @click="scrollToSection('#about')">
              <span>Über mich</span>
              <Icon icon="ph:arrow-right-bold" width="18"/>
            </button>
            <button class="cta-secondary" @click="scrollToSection('#contact')">
              <Icon icon="ph:envelope" width="18"/>
              <span>Kontakt aufnehmen</span>
            </button>
          </div>
        </div>

        <div class="hero-visual">
          <svg class="hero-pulse-line" viewBox="0 0 600 200" preserveAspectRatio="none" aria-hidden="true">
            <polyline
                class="pulse-path"
                points="0,100 140,100 165,100 180,40 195,160 210,70 225,100 600,100"
                fill="none" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"
            />
          </svg>

          <div class="staff-badge">
            <div class="badge-clip"><span class="badge-hole"></span></div>
            <div class="badge-card">
              <div class="badge-header">
                <Icon icon="ph:cross-fill" width="16" class="badge-cross"/>
                <div>
                  <span class="badge-hospital">Vinzenzkrankenhaus</span>
                  <span class="badge-hospital-sub">Hannover &middot; Elisabeth Vinzenz Verbund</span>
                </div>
              </div>

              <div class="badge-photo">
                <Icon icon="ph:user" width="56" class="avatar-icon"/>
              </div>

              <div class="badge-name">
                <span class="badge-firstname">Jonathan</span>
                <span class="badge-lastname">Sevan Werner</span>
              </div>
              <span class="badge-role">Auszubildender &middot; Pflegefachkraft</span>

              <div class="badge-barcode" aria-hidden="true"></div>
              <span class="badge-id">Seit {{ trainingStartYear }} &middot; Kirchrode</span>
            </div>
          </div>

        </div>
      </div>
    </section>

    <!-- Values Banner -->
    <section class="values-banner">
      <div class="values-track">
        <div class="value-item" v-for="value in values" :key="value.text">
          <Icon :icon="value.icon" width="20"/>
          <span>{{ value.text }}</span>
        </div>
      </div>
    </section>

    <!-- About Section -->
    <section class="about" id="about">
      <div class="section-label reveal">Über mich</div>
      <div class="about-grid">
        <div class="about-text reveal">
          <h2>Vom <span class="gradient">Jura</span> in die <span class="gradient">Pflege</span></h2>
          <p class="about-lead">
            Hey! Ich bin Jonathan, {{ calculatedAge }} Jahre alt und habe meinen ganz eigenen Weg gefunden.
          </p>
          <p>
            Nach meiner Ausbildung zum Rechtsanwaltsfachangestellten habe ich gemerkt:
            Ich möchte nicht nur mit Akten arbeiten, sondern direkt mit Menschen.
            Heute absolviere ich meine Ausbildung zur Pflegefachkraft am Vinzenzkrankenhaus
            Hannover – und liebe jeden Tag aufs Neue.
          </p>
          <p>
            Die Arbeit im Rechtswesen hat mir Struktur und Präzision beigebracht.
            Jetzt verbinde ich das mit Empathie und echter Menschlichkeit.
            Für mich die perfekte Kombination!
          </p>
          <div class="about-highlights">
            <div class="highlight">
              <span class="highlight-num">{{ trainingYear }}.</span>
              <span class="highlight-label">Ausbildungsjahr</span>
            </div>
            <div class="highlight">
              <span class="highlight-num">{{ calculatedAge }}</span>
              <span class="highlight-label">Jahre jung</span>
            </div>
            <div class="highlight">
              <span class="highlight-num">3</span>
              <span class="highlight-label">Sprachen</span>
            </div>
            <div class="highlight">
              <span class="highlight-num">{{ youthCouncilYears }}+</span>
              <span class="highlight-label">Jahre Jugendbeirat</span>
            </div>
          </div>
        </div>
        <div class="about-card reveal">
          <div class="card-inner">
            <h3>
              <Icon icon="ph:check-circle-fill" width="20" style="margin-right: 6px;"/>
              Kompetenzen & Stärken
            </h3>
            <ul class="skill-list">
              <li v-for="skill in skills" :key="skill">
                <Icon icon="ph:check-bold" width="16" class="skill-check"/>
                {{ skill }}
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <!-- Languages Banner -->
    <section class="languages-banner">
      <div class="lang-item" v-for="lang in languages" :key="lang.name">
        <Icon :icon="lang.icon" width="20"/>
        <span class="lang-name">{{ lang.name }}</span>
        <div class="lang-level-bar">
          <div class="lang-level-fill" :style="{ width: lang.level }"></div>
        </div>
        <span class="lang-desc">{{ lang.description }}</span>
      </div>
    </section>

    <!-- Journey Timeline -->
    <section class="journey" id="journey">
      <div class="section-label reveal">Mein Weg</div>
      <h2 class="section-title">Der <span class="gradient">Lebenslauf</span></h2>

      <div class="timeline">
        <div class="timeline-line"></div>

        <div
            class="timeline-item reveal"
            v-for="(item, i) in timeline"
            :key="i"
            :style="{ transitionDelay: (i * 0.1) + 's' }"
        >
          <div class="timeline-dot">
            <Icon :icon="item.icon" width="18"/>
          </div>
          <div class="timeline-content">
            <span class="timeline-date">{{ item.date }}</span>
            <h4>{{ item.title }}</h4>
            <p v-if="item.institution" class="timeline-institution">
              <Icon icon="ph:buildings" width="16"/>
              {{ item.institution }}
            </p>
            <p>{{ item.description }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Quote Section -->
    <section class="quote-section reveal">
      <div class="quote-block">
        <blockquote>
          Ich habe gelernt, dass es im Leben nicht darum geht, den geradlinigsten Weg zu gehen &ndash;
          sondern den, der dich mit Sinn erfüllt. Die Pflege ist mein Weg.
        </blockquote>
        <cite>Jonathan Sevan Werner, {{ calculatedAge }} Jahre</cite>
      </div>
    </section>

    <!-- Contact Section -->
    <section class="contact" id="contact">
      <div class="section-label reveal">Kontakt</div>
      <div class="contact-grid">
        <div class="contact-info reveal">
          <h2>Schreib <span class="gradient">mir</span></h2>
          <p>
            Egal ob du Fragen zur Pflegeausbildung hast, dich über meinen Weg
            vom Jura-Studenten zur Pflegekraft wunderst oder einfach connecten möchtest –
            ich freue mich auf deine Nachricht!
          </p>
          <div class="contact-details">
            <a :href="`mailto:${contact.email}`" class="contact-item">
              <Icon icon="ph:envelope-fill" width="20"/>
              <span>{{ contact.email }}</span>
            </a>
          </div>

          <!-- Kontaktformular -->
          <form class="contact-form" @submit.prevent="onSubmit" novalidate>
            <!-- Honeypot – unsichtbar für Menschen -->
            <div class="honeypot" aria-hidden="true">
              <input type="text" v-model="form.website" tabindex="-1" autocomplete="off"/>
            </div>

            <div class="form-group">
              <input
                  type="text"
                  v-model="form.name"
                  placeholder="Dein Name *"
                  :class="{ error: errors.name }"
                  @input="errors.name = ''"
              />
              <span v-if="errors.name" class="form-error">{{ errors.name }}</span>
            </div>

            <div class="form-group">
              <input
                  type="email"
                  v-model="form.email"
                  placeholder="Deine E-Mail *"
                  :class="{ error: errors.email }"
                  @input="errors.email = ''"
              />
              <span v-if="errors.email" class="form-error">{{ errors.email }}</span>
            </div>

            <div class="form-group">
              <input
                  type="text"
                  v-model="form.subject"
                  placeholder="Betreff *"
                  :class="{ error: errors.subject }"
                  @input="errors.subject = ''"
              />
              <span v-if="errors.subject" class="form-error">{{ errors.subject }}</span>
            </div>

            <div class="form-group">
              <textarea
                  v-model="form.message"
                  rows="6"
                  placeholder="Deine Nachricht *"
                  :class="{ error: errors.message }"
                  @input="errors.message = ''"
              ></textarea>
              <span v-if="errors.message" class="form-error">{{ errors.message }}</span>
            </div>

            <label class="privacy-check">
              <input type="checkbox" v-model="form.privacyAccepted"/>
              <span>Ich stimme der Verarbeitung meiner Daten zur Kontaktaufnahme zu.</span>
            </label>
            <span v-if="errors.privacyAccepted" class="form-error">{{ errors.privacyAccepted }}</span>

            <button type="submit" class="cta-primary" :disabled="isSubmitting">
              <Icon v-if="isSubmitting" icon="ph:spinner" width="18" class="spinner"/>
              {{ isSubmitting ? 'Wird gesendet...' : 'Nachricht senden' }}
            </button>

            <p v-if="submitSuccess" class="success-message">
              <Icon icon="ph:check-circle-fill" width="18"/>
              Danke! Deine Nachricht wurde erfolgreich versendet.
            </p>

            <p v-if="submitError" class="error-message">
              <Icon icon="ph:warning-circle-fill" width="18"/>
              {{ submitError }}
            </p>
          </form>
        </div>

        <div class="contact-visual reveal">
          <div class="contact-card-visual">
            <div class="ccv-top">
              <Icon icon="ph:hospital" width="32"/>
              <span class="ccv-label">Vinzenzkrankenhaus</span>
              <span class="ccv-city">Hannover</span>
            </div>
            <div class="hospital-ekg">
              <svg viewBox="0 0 200 60" class="mini-ekg">
                <polyline points="0,30 40,30 50,30 55,10 60,50 65,30 200,30"
                          fill="none" stroke="currentColor" stroke-width="2"/>
              </svg>
            </div>
            <div class="ccv-info">
              <span>
                <Icon icon="ph:star-fill" width="14"/>
                Pflegefachkraft i.A.
              </span>
              <span>
                <Icon icon="ph:calendar-check" width="14"/>
                Ausbildungsstart {{ trainingStartYear }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Footer -->
    <footer class="footer">
      <div class="footer-inner">
        <div class="footer-brand">
          <h3>Jonathan Sevan Werner</h3>
          <p>
            <Icon icon="ph:heart-fill" width="14"/>
            Pflege mit Herz & Verstand
          </p>
        </div>
        <div class="footer-center">
          <span>© {{ currentYear }} – Alle Rechte vorbehalten</span>
        </div>
        <div class="footer-right">
          <a href="/impressum">Impressum</a>
          <a href="/datenschutz">Datenschutz</a>
        </div>
      </div>
      <div class="footer-credit">
        <span>
          Erstellt von
          <a href="https://zirke-digital.de" target="_blank" rel="noopener noreferrer">
            <Icon icon="ph:sparkle" width="14"/>
            zirke-digital
          </a>
        </span>
      </div>
    </footer>
  </div>
</template>

<script>
import {ref, reactive, computed, onMounted, onUnmounted} from 'vue'
import {Icon} from '@iconify/vue'
import {sendContactRequest} from '@/services/contactApi'

export default {
  name: 'HomeView',
  components: {Icon},
  setup() {
    const menuOpen = ref(false)
    const scrolled = ref(false)
    const theme = ref('light')

    // Contact form
    let formStartedAt = Date.now()

    const form = reactive({
      name: '',
      email: '',
      subject: '',
      message: '',
      privacyAccepted: false,
      website: ''
    })

    const errors = reactive({
      name: '',
      email: '',
      subject: '',
      message: '',
      privacyAccepted: ''
    })

    const isSubmitting = ref(false)
    const submitSuccess = ref(false)
    const submitError = ref('')

    // Auto-calculated data
    const birthDate = new Date('2007-07-03')
    const trainingStartDate = new Date('2026-04-01')
    const youthCouncilStartDate = new Date('2021-10-01')
    const today = new Date()
    const currentYear = today.getFullYear()

    // Calculate age precisely
    const calculatedAge = computed(() => {
      let age = today.getFullYear() - birthDate.getFullYear()
      const monthDiff = today.getMonth() - birthDate.getMonth()
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--
      }
      return age
    })

    // Calculate training year
    const trainingYear = computed(() => {
      let years = today.getFullYear() - trainingStartDate.getFullYear()
      const monthDiff = today.getMonth() - trainingStartDate.getMonth()
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < trainingStartDate.getDate())) {
        years--
      }
      return Math.max(1, years + 1)
    })

    const trainingStartYear = computed(() => trainingStartDate.getFullYear())

    const youthCouncilYears = computed(() => {
      let years = today.getFullYear() - youthCouncilStartDate.getFullYear()
      const monthDiff = today.getMonth() - youthCouncilStartDate.getMonth()
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < youthCouncilStartDate.getDate())) {
        years--
      }
      return years
    })

    // Toggle theme
    const toggleTheme = () => {
      theme.value = theme.value === 'dark' ? 'light' : 'dark'
      localStorage.setItem('theme', theme.value)
    }

    // Theme: Standard ist immer Light, außer der Nutzer hat bereits
    // explizit auf Dark umgeschaltet (in localStorage gemerkt).
    const savedTheme = localStorage.getItem('theme')
    if (savedTheme === 'dark') {
      theme.value = 'dark'
    }

    const menuLinks = [
      {name: 'Über mich', href: '#about'},
      {name: 'Lebenslauf', href: '#journey'},
      {name: 'Kontakt', href: '#contact'}
    ]

    const values = [
      {icon: 'ph:heart-straight-fill', text: 'Empathie'},
      {icon: 'ph:users-three-fill', text: 'Teamwork'},
      {icon: 'ph:books-fill', text: 'Lernbereitschaft'},
      {icon: 'ph:target-fill', text: 'Präzision'},
      {icon: 'ph:star-fill', text: 'Wertschätzung'},
      {icon: 'ph:hand-heart-fill', text: 'Hingabe'}
    ]

    const skills = [
      'Grund- & Behandlungspflege',
      'Patientenbeobachtung & -dokumentation',
      'Medikamentenmanagement',
      'Wundversorgung & Verbandswechsel',
      'Mobilisation & Prophylaxen',
      'Pflegeplanung & Pflegedokumentation',
      'Kommunikation mit Angehörigen',
      'Interdisziplinäre Teamarbeit',
      'Microsoft Office & BeA',
      'DATEV-Kenntnisse',
      'Schulsanitäter-Erfahrung',
      'Teamfähigkeit & Empathie'
    ]

    const languages = [
      {name: 'Deutsch', level: '100%', description: 'Muttersprache', icon: 'ph:flag-banner-fold-fill'},
      {name: 'Englisch', level: '85%', description: 'Verhandlungssicher', icon: 'ph:flag-banner-fold-fill'},
      {name: 'Türkisch', level: '80%', description: 'Fließend', icon: 'ph:flag-banner-fold-fill'}
    ]

    const timeline = [
      {
        date: 'April 2026 – Heute',
        title: 'Ausbildung zur Pflegefachkraft',
        institution: 'Vinzenzkrankenhaus Hannover',
        description: 'Beginn der Ausbildung mit vollem Fokus auf die Pflege – der Start in meine berufliche Zukunft.',
        icon: 'ph:heartbeat-fill'
      },
      {
        date: 'August 2023 – Februar 2026',
        title: 'Ausbildung zum Rechtsanwaltsfachangestellten',
        institution: 'Wolff Rechtsanwälte, Laatzen',
        description: 'Einjährige Ausbildung mit wertvollen Einblicken in juristische Arbeitsabläufe.',
        icon: 'ph:scales-fill'
      },
      {
        date: 'Oktober 2023',
        title: 'Betriebspraktikum',
        institution: 'Wolff Rechtsanwälte, Laatzen',
        description: 'Vertiefung der praktischen Kenntnisse im Rechtswesen.',
        icon: 'ph:briefcase-fill'
      },
      {
        date: `Oktober 2021 – Heute (${youthCouncilYears.value} Jahre)`,
        title: 'Jugendbeirat der Stadt Laatzen',
        institution: 'Stadt Laatzen',
        description: 'Ehrenamtliches Engagement im Jugendbeirat – aktiv für die Jugend in Laatzen.',
        icon: 'ph:users-fill'
      },
      {
        date: 'April – Oktober 2022',
        title: 'Betriebspraktikum',
        institution: 'K2-Modernisierung GmbH, Hannover',
        description: 'Einblicke in Verwaltungsaufgaben eines handwerklichen Betriebes.',
        icon: 'ph:buildings-fill'
      },
      {
        date: 'Februar 2022',
        title: 'Praktikum im Kinder- und Jugendbüro',
        institution: 'Stadt Laatzen',
        description: 'Unterstützung der Sozialpädagogen bei Hausaufgabenhilfe.',
        icon: 'ph:chalkboard-teacher-fill'
      }
    ]

    const contact = {
      email: 'mail@js-werner.de',
      phone: '0176 32875205',
      address: 'Wülferoderstraße 27, 30880 Laatzen',
      birthday: '03. Juli 2007'
    }

    const scrollToSection = (selector) => {
      const el = document.querySelector(selector)
      if (el) {
        el.scrollIntoView({behavior: 'smooth', block: 'start'})
      }
    }

    const handleScroll = () => {
      scrolled.value = window.scrollY > 50
    }

    // ===== Form Validation =====
    const validateForm = () => {
      let valid = true
      errors.name = ''
      errors.email = ''
      errors.subject = ''
      errors.message = ''
      errors.privacyAccepted = ''

      if (!form.name || form.name.length < 2) {
        errors.name = 'Bitte gib deinen Namen an (mind. 2 Zeichen).'
        valid = false
      }

      if (!form.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
        errors.email = 'Bitte gib eine gültige E-Mail-Adresse an.'
        valid = false
      }

      if (!form.subject || form.subject.length < 3) {
        errors.subject = 'Bitte gib einen Betreff an (mind. 3 Zeichen).'
        valid = false
      }

      if (!form.message || form.message.length < 10) {
        errors.message = 'Bitte gib eine Nachricht ein (mind. 10 Zeichen).'
        valid = false
      }

      if (!form.privacyAccepted) {
        errors.privacyAccepted = 'Bitte akzeptiere die Datenschutzbestimmungen.'
        valid = false
      }

      return valid
    }

    const onSubmit = async () => {
      // Honeypot-Check
      if (form.website) {
        submitSuccess.value = true
        form.name = ''
        form.email = ''
        form.subject = ''
        form.message = ''
        form.privacyAccepted = false
        form.website = ''
        return
      }

      if (!validateForm()) return

      isSubmitting.value = true
      submitError.value = ''
      submitSuccess.value = false

      try {
        await sendContactRequest({
          name: form.name,
          email: form.email,
          subject: form.subject,
          message: form.message,
          formStartedAt
        })

        submitSuccess.value = true
        form.name = ''
        form.email = ''
        form.subject = ''
        form.message = ''
        form.privacyAccepted = false
        form.website = ''
        formStartedAt = Date.now()
      } catch (error) {
        submitError.value = 'Deine Nachricht konnte nicht gesendet werden. Bitte versuche es später erneut.'
      } finally {
        isSubmitting.value = false
      }
    }

    let revealObserver = null

    onMounted(() => {
      window.addEventListener('scroll', handleScroll)

      revealObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                entry.target.classList.add('is-visible')
                revealObserver.unobserve(entry.target)
              }
            })
          },
          {threshold: 0.15, rootMargin: '0px 0px -40px 0px'}
      )
      document.querySelectorAll('.reveal').forEach((el) => revealObserver.observe(el))
    })

    onUnmounted(() => {
      window.removeEventListener('scroll', handleScroll)
      if (revealObserver) revealObserver.disconnect()
    })

    return {
      menuOpen,
      scrolled,
      theme,
      calculatedAge,
      trainingYear,
      trainingStartYear,
      youthCouncilYears,
      currentYear,
      toggleTheme,
      menuLinks,
      values,
      skills,
      languages,
      timeline,
      contact,
      scrollToSection,
      form,
      errors,
      isSubmitting,
      submitSuccess,
      submitError,
      onSubmit
    }
  }
}
</script>

<style>
/* ===== CSS Reset & Variables ===== */
*, *::before, *::after {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

h1, h2, h3, h4, p {
  overflow-wrap: break-word;
  word-break: break-word;
  hyphens: auto;
}

/* ===== Theme Variables ===== */
.app.dark {
  --bg: #15120f;
  --surface: #211c17;
  --surface-light: #2b241d;
  --text: #f7f1e8;
  --text-secondary: #cbbfae;
  --text-muted: #8f8272;
  --accent: #5fd1c4;
  --accent-light: rgba(95, 209, 196, 0.16);
  --accent-glow: rgba(95, 209, 196, 0.32);
  --warm: #f0996e;
  --warm-light: rgba(240, 153, 110, 0.16);
  --border: #3d332a;
}

.app.light {
  --bg: #fbf7f2;
  --surface: #ffffff;
  --surface-light: #f3ede4;
  --text: #2b2420;
  --text-secondary: #6b5d4f;
  --text-muted: #9a8c7c;
  --accent: #1f7a73;
  --accent-light: rgba(31, 122, 115, 0.1);
  --accent-glow: rgba(31, 122, 115, 0.18);
  --warm: #d97757;
  --warm-light: rgba(217, 119, 87, 0.12);
  --border: #e8dfd2;
}

.app {
  --radius: 16px;
  --radius-lg: 24px;
  --transition: 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  background: var(--bg);
  color: var(--text);
  min-height: 100vh;
  transition: background 0.4s, color 0.4s;
}

html {
  scroll-behavior: smooth;
}

body {
  background: var(--bg);
  color: var(--text);
  line-height: 1.7;
  overflow-x: hidden;
  -webkit-font-smoothing: antialiased;
  transition: background 0.4s, color 0.4s;
}

a {
  color: inherit;
  text-decoration: none;
}

/* ===== Navigation ===== */
.nav {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  padding: 1.5rem 3rem;
  z-index: 100;
  transition: var(--transition);
  background: transparent;
}

.nav.scrolled {
  background: var(--bg);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid var(--border);
  padding: 1rem 3rem;
}

.nav-actions {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-shrink: 0;
}

.theme-toggle {
  background: var(--surface);
  border: 1px solid var(--border);
  color: var(--text);
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: var(--transition);
}

.theme-toggle:hover {
  border-color: var(--accent);
  color: var(--accent);
}

.nav-logo {
  font-size: clamp(1rem, 4vw, 1.4rem);
  font-weight: 800;
  letter-spacing: -0.5px;
  display: flex;
  align-items: center;
  gap: 10px;
  white-space: nowrap;
  flex-shrink: 1;
}

.logo-pulse {
  width: 10px;
  height: 10px;
  background: var(--accent);
  border-radius: 50%;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.4;
    transform: scale(1.8);
  }
}

.logo-dot {
  color: var(--accent);
}

.nav-menu-icon {
  display: flex;
  flex-direction: column;
  gap: 6px;
  cursor: pointer;
  padding: 8px;
  z-index: 101;
}

.nav-menu-icon span {
  display: block;
  width: 28px;
  height: 2px;
  background: var(--text);
  border-radius: 2px;
  transition: var(--transition);
}

.menu-open .nav-menu-icon span:first-child {
  transform: translateY(8px) rotate(45deg);
}

.menu-open .nav-menu-icon span:last-child {
  transform: translateY(-8px) rotate(-45deg);
}

/* ===== Menu Overlay ===== */
.menu-overlay {
  position: fixed;
  inset: 0;
  background: var(--bg);
  backdrop-filter: blur(30px);
  z-index: 99;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3rem;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.4s ease;
}

.menu-open .menu-overlay {
  opacity: 1;
  pointer-events: all;
}

.menu-links {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  text-align: center;
}

.menu-links a {
  font-size: 3rem;
  font-weight: 800;
  opacity: 0;
  transform: translateY(20px);
  transition: var(--transition);
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.8rem;
}

.menu-open .menu-links a {
  opacity: 1;
  transform: translateY(0);
}

.menu-links a:hover {
  color: var(--accent);
}


.menu-arrow {
  opacity: 0;
  transition: var(--transition);
}

.menu-links a:hover .menu-arrow {
  opacity: 1;
}

.menu-decoration {
  position: absolute;
  bottom: 3rem;
}

.ekg-svg {
  width: 200px;
  color: var(--accent);
  opacity: 0.3;
}

/* ===== Hero Section ===== */
.hero {
  min-height: 100vh;
  display: flex;
  align-items: center;
  padding: 6rem 3rem 3rem;
  position: relative;
  overflow: hidden;
}

.hero::after {
  content: '';
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
  opacity: 0.5;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.05'/%3E%3C/svg%3E");
}

.hero::before {
  content: '';
  position: absolute;
  top: -300px;
  right: -200px;
  width: 700px;
  height: 700px;
  background: radial-gradient(circle, var(--accent-light) 0%, transparent 70%);
  border-radius: 50%;
  z-index: 0;
}

.hero-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  max-width: 1300px;
  margin: 0 auto;
  width: 100%;
  position: relative;
  z-index: 2;
}

.hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: var(--surface);
  border: 1px solid var(--border);
  padding: 10px 22px;
  border-radius: 50px;
  font-size: 0.88rem;
  font-weight: 500;
  color: var(--text-secondary);
  margin-bottom: 2rem;
}

.badge-dot {
  width: 8px;
  height: 8px;
  flex-shrink: 0;
  background: var(--warm);
  border-radius: 50%;
  box-shadow: 0 0 8px var(--warm-light);
  animation: pulse 2s infinite;
}

.badge-separator {
  color: var(--border);
}

.hero-name {
  font-size: clamp(3.5rem, 7vw, 6rem);
  font-weight: 900;
  line-height: 1.05;
  margin-bottom: 1.5rem;
}

.name-line {
  display: block;
}

.name-line.outline {
  color: var(--text-secondary);
  font-weight: 700;
}

.name-line.filled {
  color: var(--accent);
}

.hero-title {
  font-size: 1.2rem;
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 1rem;
  font-weight: 500;
}

.title-icon {
  display: inline-flex;
  align-items: center;
  color: var(--warm);
  flex-shrink: 0;
}

.hero-motto {
  font-size: 1.15rem;
  color: var(--text-muted);
  margin-bottom: 2rem;
  max-width: 480px;
  font-style: italic;
}

.hero-stats {
  display: flex;
  gap: 2.5rem;
  margin-bottom: 2.5rem;
  flex-wrap: wrap;
}

.hero-stat {
  text-align: center;
}

.stat-number {
  display: block;
  font-size: 2rem;
  font-weight: 900;
  color: var(--accent);
  line-height: 1.2;
}

.stat-label {
  font-size: 0.8rem;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 1.5px;
}

.hero-cta {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.cta-primary {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 1rem 2.2rem;
  background: var(--accent);
  color: white;
  border: none;
  border-radius: 50px;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: var(--transition);
  box-shadow: 0 4px 24px var(--accent-glow);
}

.cta-primary:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 36px var(--accent-glow);
}

.cta-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.cta-secondary {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 1rem 2.2rem;
  background: transparent;
  color: var(--text);
  border: 2px solid var(--border);
  border-radius: 50px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: var(--transition);
}

.cta-secondary:hover {
  border-color: var(--accent);
  background: var(--accent-light);
}

/* ===== Hero Visual ===== */
.hero-visual {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

/* ===== Scroll Reveal ===== */
.reveal {
  opacity: 0;
  transform: translateY(28px);
  transition: opacity 0.7s cubic-bezier(0.25, 0.1, 0.25, 1), transform 0.7s cubic-bezier(0.25, 0.1, 0.25, 1);
}

.reveal.is-visible {
  opacity: 1;
  transform: translateY(0);
}

/* ===== Hero pulse line (animated EKG trace) ===== */
.hero-pulse-line {
  position: absolute;
  top: 50%;
  left: -10%;
  width: 120%;
  height: 200px;
  transform: translateY(-50%);
  z-index: 0;
  pointer-events: none;
  opacity: 0.35;
}

.pulse-path {
  stroke: var(--accent);
  stroke-dasharray: 900;
  stroke-dashoffset: 900;
  animation: drawPulse 4.5s ease-in-out infinite;
}

@keyframes drawPulse {
  0% {
    stroke-dashoffset: 900;
    opacity: 0;
  }
  8% {
    opacity: 1;
  }
  55% {
    stroke-dashoffset: 0;
    opacity: 1;
  }
  75% {
    opacity: 0;
  }
  100% {
    stroke-dashoffset: -900;
    opacity: 0;
  }
}

/* ===== Staff Badge (hero centerpiece) ===== */
.staff-badge {
  position: relative;
  width: 260px;
  z-index: 2;
  transform-origin: top center;
  animation: badgeSwing 6s ease-in-out infinite;
}

@keyframes badgeSwing {
  0%, 100% {
    transform: rotate(-2.5deg);
  }
  50% {
    transform: rotate(2.5deg);
  }
}

.badge-clip {
  width: 64px;
  height: 20px;
  margin: 0 auto -2px;
  background: linear-gradient(180deg, var(--surface-light), var(--border));
  border: 1px solid var(--border);
  border-radius: 6px 6px 3px 3px;
  position: relative;
  z-index: 1;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
}

.badge-hole {
  position: absolute;
  top: 5px;
  left: 50%;
  transform: translateX(-50%);
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--bg);
  border: 1px solid var(--border);
}

.badge-card {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 18px;
  padding: 1.75rem 1.5rem 1.5rem;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.14);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.badge-header {
  display: flex;
  align-items: center;
  gap: 8px;
  align-self: flex-start;
  margin-bottom: 1.25rem;
  text-align: left;
}

.badge-cross {
  color: var(--warm);
  flex-shrink: 0;
}

.badge-hospital {
  display: block;
  font-size: 0.78rem;
  font-weight: 800;
  color: var(--text);
  line-height: 1.2;
}

.badge-hospital-sub {
  display: block;
  font-size: 0.62rem;
  color: var(--text-muted);
  line-height: 1.2;
}

.badge-photo {
  width: 96px;
  height: 96px;
  border-radius: 50%;
  background: var(--surface-light);
  border: 3px solid var(--accent-light);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
}

.avatar-icon {
  color: var(--accent);
  opacity: 0.6;
}

.badge-name {
  display: flex;
  flex-direction: column;
  line-height: 1.15;
  margin-bottom: 0.3rem;
}

.badge-firstname {
  font-size: 1rem;
  font-weight: 500;
  color: var(--text-secondary);
}

.badge-lastname {
  font-size: 1.3rem;
  font-weight: 800;
  color: var(--text);
}

.badge-role {
  font-size: 0.78rem;
  color: var(--accent);
  font-weight: 700;
  margin-bottom: 1.5rem;
}

.badge-barcode {
  width: 100%;
  height: 28px;
  margin-bottom: 0.6rem;
  border-radius: 4px;
  background: repeating-linear-gradient(
      90deg,
      var(--text) 0px, var(--text) 2px,
      transparent 2px, transparent 4px,
      var(--text) 4px, var(--text) 5px,
      transparent 5px, transparent 9px,
      var(--text) 9px, var(--text) 12px,
      transparent 12px, transparent 14px,
      var(--text) 14px, var(--text) 15px,
      transparent 15px, transparent 20px
  );
  opacity: 0.55;
}

.badge-id {
  font-size: 0.68rem;
  color: var(--text-muted);
  letter-spacing: 0.5px;
}


/* ===== Values Banner ===== */
.values-banner {
  display: flex;
  justify-content: center;
  padding: 2rem 3rem;
  background: var(--surface);
  border-top: 1px solid var(--border);
  border-bottom: 1px solid var(--border);
}

.values-track {
  display: flex;
  gap: 3rem;
  flex-wrap: wrap;
  justify-content: center;
}

.value-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  color: var(--text-secondary);
  font-size: 0.95rem;
}

.value-item :deep(svg) {
  color: var(--accent);
}

/* ===== Languages Banner ===== */
.languages-banner {
  display: flex;
  gap: 2rem;
  justify-content: center;
  padding: 2.5rem 3rem;
  background: var(--surface-light);
  flex-wrap: wrap;
  align-items: center;
}

.lang-item {
  display: flex;
  align-items: center;
  gap: 12px;
  color: var(--text-secondary);
}

.lang-name {
  font-weight: 700;
  font-size: 0.9rem;
  min-width: 70px;
}

.lang-level-bar {
  width: 120px;
  height: 5px;
  background: var(--border);
  border-radius: 10px;
  overflow: hidden;
}

.lang-level-fill {
  height: 100%;
  background: var(--accent);
  border-radius: 10px;
  transition: width 1s ease;
}

.lang-desc {
  font-size: 0.8rem;
  color: var(--text-muted);
}

/* ===== About Section ===== */
.about {
  padding: 7rem 3rem;
  max-width: 1300px;
  margin: 0 auto;
}

.section-label {
  font-size: 0.85rem;
  font-weight: 700;
  color: var(--accent);
  text-transform: uppercase;
  letter-spacing: 2px;
  margin-bottom: 1rem;
}

.about-grid {
  display: grid;
  grid-template-columns: 1.2fr 0.8fr;
  gap: 4rem;
  align-items: start;
}

.about-text h2 {
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 800;
  margin-bottom: 1.5rem;
}

.gradient {
  color: var(--accent);
  position: relative;
  white-space: nowrap;
}

.gradient::after {
  content: '';
  position: absolute;
  left: -2%;
  right: -2%;
  bottom: -0.08em;
  height: 0.22em;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 12' preserveAspectRatio='none'%3E%3Cpath d='M0 8 Q 15 2, 30 7 T 60 6 T 100 5' fill='none' stroke='%23D97757' stroke-width='3' stroke-linecap='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-size: 100% 100%;
  opacity: 0.6;
}

.app.dark .gradient::after {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 12' preserveAspectRatio='none'%3E%3Cpath d='M0 8 Q 15 2, 30 7 T 60 6 T 100 5' fill='none' stroke='%23F0996E' stroke-width='3' stroke-linecap='round'/%3E%3C/svg%3E");
}

.about-lead {
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--text);
  margin-bottom: 1rem;
}

.about-text p {
  color: var(--text-secondary);
  font-size: 1.05rem;
  margin-bottom: 1.5rem;
  max-width: 600px;
}

.about-highlights {
  display: flex;
  gap: 2.5rem;
  margin-top: 2rem;
  flex-wrap: wrap;
}

.highlight {
  text-align: center;
}

.highlight-num {
  display: block;
  font-size: 2.8rem;
  font-weight: 900;
  color: var(--accent);
}

.highlight-label {
  font-size: 0.85rem;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 1px;
}

.about-card {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  padding: 2rem;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06);
  position: sticky;
  top: 6rem;
}

.card-inner h3 {
  font-size: 1.2rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  color: var(--accent);
  display: flex;
  align-items: center;
}

.skill-list {
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
}

.skill-list li {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.93rem;
  color: var(--text-secondary);
}

.skill-check {
  color: var(--warm);
  flex-shrink: 0;
}

/* ===== Journey / Timeline ===== */
.journey {
  padding: 7rem 3rem;
  background: var(--surface);
}

.section-title {
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 800;
  margin-bottom: 3rem;
}

.section-label, .section-title, .timeline {
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
}

.timeline {
  position: relative;
  padding-left: 50px;
}

.timeline-line {
  position: absolute;
  left: 20px;
  top: 0;
  bottom: 0;
  width: 12px;
  transform: translateX(-5px);
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='48' viewBox='0 0 12 48'%3E%3Cpath d='M6 0 L6 16 L3 18 L9 26 L6 30 L6 48' fill='none' stroke='%23E8DFD2' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: repeat-y;
  background-position: center;
  background-size: 12px 48px;
}

.timeline-item {
  position: relative;
  margin-bottom: 2rem;
}

.timeline-item:last-child {
  margin-bottom: 0;
}

.timeline-dot {
  position: absolute;
  left: -50px;
  top: 4px;
  width: 44px;
  height: 44px;
  background: var(--surface);
  border: 3px solid var(--accent);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  color: var(--accent);
  box-shadow: 0 0 0 8px var(--accent-light);
}

.timeline-content {
  background: var(--surface-light);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 1rem 2rem;
  transition: var(--transition);
  margin-left: 0.5rem;
}

.timeline-content:hover {
  border-color: var(--accent);
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06);
  transform: translateX(6px);
}

.timeline-date {
  font-size: 0.8rem;
  color: var(--accent);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 2px;
}

.timeline-content h4 {
  font-size: 1.2rem;
  font-weight: 700;
  margin: 0.4rem 0 0.2rem;
}

.timeline-institution {
  font-size: 0.9rem;
  color: var(--text-muted);
  margin-bottom: 0.5rem;
  font-style: italic;
  display: flex;
  align-items: center;
  gap: 6px;
}

.timeline-content p {
  color: var(--text-secondary);
  font-size: 0.95rem;
}

/* ===== Quote Section ===== */
.quote-section {
  padding: 6rem 3rem;
  display: flex;
  justify-content: center;
}

.quote-block {
  max-width: 640px;
  border-left: 3px solid var(--warm);
  padding-left: 2rem;
}

.quote-block blockquote {
  font-size: 1.6rem;
  font-weight: 600;
  color: var(--text);
  line-height: 1.5;
  letter-spacing: -0.01em;
  margin-bottom: 1.25rem;
}

.quote-block cite {
  font-weight: 600;
  color: var(--text-muted);
  font-style: normal;
  font-size: 0.9rem;
}

/* ===== Contact Section ===== */
.contact {
  padding: 7rem 3rem;
  background: var(--surface);
}

.contact-grid {
  max-width: 1100px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: start;
}

.contact-info h2 {
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 800;
  margin-bottom: 1rem;
}

.contact-info > p {
  color: var(--text-secondary);
  margin-bottom: 2rem;
  font-size: 1.05rem;
}

.contact-details {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 2rem;
}

.contact-item {
  display: flex;
  align-items: center;
  gap: 12px;
  font-weight: 500;
  color: var(--text-secondary);
  transition: color 0.2s;
}

a.contact-item:hover {
  color: var(--accent);
}

.contact-item :deep(svg) {
  color: var(--accent);
  flex-shrink: 0;
}

/* ===== Contact Form ===== */
.contact-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.honeypot {
  position: absolute;
  opacity: 0;
  height: 0;
  width: 0;
  z-index: -1;
  pointer-events: none;
  overflow: hidden;
}

.honeypot input {
  width: 0;
  height: 0;
  padding: 0;
  border: none;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group input,
.form-group textarea {
  padding: 0.9rem 1rem;
  background: var(--surface-light);
  border: 1px solid var(--border);
  border-radius: 12px;
  color: var(--text);
  font-size: 0.95rem;
  font-family: inherit;
  transition: border-color 0.3s;
}

.form-group input:focus,
.form-group textarea:focus {
  outline: none;
  border-color: var(--accent);
  box-shadow: 0 0 0 3px var(--accent-light);
}

.form-group input.error,
.form-group textarea.error {
  border-color: #ef4444;
}

.form-error {
  color: #ef4444;
  font-size: 0.8rem;
  margin-top: 0.25rem;
  padding-left: 0.25rem;
}

.privacy-check {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 0.88rem;
  color: var(--text-secondary);
  cursor: pointer;
}

.privacy-check input {
  margin-top: 3px;
  accent-color: var(--accent);
}

.spinner {
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.success-message {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--warm);
  font-weight: 500;
  padding: 0.75rem;
  background: var(--warm-light);
  border-radius: 12px;
  margin-top: 0.5rem;
}

.error-message {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #ef4444;
  font-weight: 500;
  padding: 0.75rem;
  background: rgba(239, 68, 68, 0.1);
  border-radius: 12px;
  margin-top: 0.5rem;
}

.contact-visual {
  display: flex;
  justify-content: center;
}

.contact-card-visual {
  background: var(--surface-light);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  padding: 3rem;
  text-align: center;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06);
  width: 100%;
  max-width: 350px;
}

.ccv-top {
  margin-bottom: 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.3rem;
}

.ccv-top :deep(svg) {
  color: var(--accent);
  margin-bottom: 0.5rem;
}

.ccv-label {
  font-weight: 700;
  font-size: 1.2rem;
}

.ccv-city {
  color: var(--text-muted);
  font-size: 0.9rem;
}

.hospital-ekg {
  margin-bottom: 1.5rem;
}

.mini-ekg {
  width: 180px;
  height: 50px;
  color: var(--accent);
}

.ccv-info {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  font-size: 0.9rem;
  color: var(--text-secondary);
}

.ccv-info span {
  display: flex;
  align-items: center;
  gap: 6px;
  justify-content: center;
}

.ccv-info :deep(svg) {
  color: var(--accent);
}

/* ===== Footer ===== */
.footer {
  background: var(--bg);
  color: var(--text-secondary);
  padding: 2.5rem 3rem 1.5rem;
  border-top: 1px solid var(--border);
}

.footer-inner {
  max-width: 1300px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1.5rem;
}

.footer-brand h3 {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--text);
}

.footer-brand p {
  font-size: 0.85rem;
  color: var(--text-muted);
  display: flex;
  align-items: center;
  gap: 6px;
}

.footer-center span {
  font-size: 0.85rem;
}

.footer-right {
  display: flex;
  gap: 1.5rem;
}

.footer-right a {
  font-size: 0.85rem;
  transition: color 0.2s;
}

.footer-right a:hover {
  color: var(--accent);
}

.footer-credit {
  max-width: 1300px;
  margin: 1.5rem auto 0;
  padding-top: 1.5rem;
  border-top: 1px solid var(--border);
  text-align: center;
  font-size: 0.8rem;
  color: var(--text-muted);
}

.footer-credit a {
  color: var(--accent);
  font-weight: 600;
  transition: opacity 0.2s;
}

.footer-credit a:hover {
  opacity: 0.8;
}

/* ===== Responsive ===== */
@media (max-width: 1024px) {
  .hero-grid {
    grid-template-columns: 1fr;
    text-align: center;
  }

  .hero-motto {
    margin: 0 auto 2rem;
  }

  .hero-cta {
    justify-content: center;
  }

  .hero-title {
    justify-content: center;
  }

  .hero-stats {
    justify-content: center;
  }

  .hero-badge {
    justify-content: center;
  }

  .hero-visual {
    order: -1;
    margin-top: 2rem;
  }

  .staff-badge {
    width: 230px;
  }

  .about-grid {
    grid-template-columns: 1fr;
  }

  .about-card {
    position: static;
  }

  .contact-grid {
    grid-template-columns: 1fr;
  }

  .languages-banner {
    gap: 1.5rem;
  }

  .lang-level-bar {
    width: 80px;
  }
}

@media (max-width: 768px) {
  .nav {
    padding: 1rem 1.5rem;
  }

  .hero {
    padding: 5rem 1.5rem 2rem;
  }

  .about, .journey, .contact, .quote-section {
    padding: 4rem 1.5rem;
  }

  .values-banner {
    padding: 1.5rem;
  }

  .values-track {
    gap: 1.5rem;
  }

  .menu-links a {
    font-size: 2rem;
  }

  .about-highlights {
    gap: 1.5rem;
  }

  .highlight-num {
    font-size: 2rem;
  }

  .timeline {
    padding-left: 40px;
  }

  .timeline-dot {
    left: -28px;
    width: 36px;
    height: 36px;
  }

  .timeline-line {
    left: 29px;
  }

  .timeline-content {
    padding: 1.2rem 1.5rem;
  }

  .quote-block {
    padding-left: 1.25rem;
  }

  .quote-block blockquote {
    font-size: 1.2rem;
  }

  .footer-inner {
    flex-direction: column;
    text-align: center;
  }
}
</style>